/**
 * Lift Sets UK — Lead Capture Shared Utility
 * File: assets/lead-capture-utils.js
 * 
 * Load in theme.liquid BEFORE individual popup scripts:
 * <script data-disposable-domains="{{ 'disposable-domains.json' | asset_url }}"
 *   src="{{ 'lead-capture-utils.js' | asset_url }}" defer></script>
 *
 * All capture layers (welcome popup, exit-intent, sticky bar, 
 * inline capture, back-in-stock) use this shared module.
 */

window.LiftSetsCapture = (function () {
  'use strict';

  /* ============================================================
     STORAGE HELPERS
     ============================================================ */

  function getLocal(key) {
    try { return localStorage.getItem(key); }
    catch (e) { return null; }
  }

  function setLocal(key, value) {
    try { localStorage.setItem(key, value); }
    catch (e) {}
  }

  function removeLocal(key) {
    try { localStorage.removeItem(key); }
    catch (e) {}
  }

  function getSession(key) {
    try { return sessionStorage.getItem(key); }
    catch (e) { return null; }
  }

  function setSession(key, value) {
    try { sessionStorage.setItem(key, value); }
    catch (e) {}
  }


  /* ============================================================
     EMAIL VALIDATION
     ============================================================ */

  var emailRegex = /^[a-zA-Z0-9.!#$%&'*+\/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;

  function isValidEmail(email) {
    if (!email || typeof email !== 'string') return false;
    email = email.trim();
    return emailRegex.test(email) && email.includes('.') && email.length <= 254;
  }


  /* ============================================================
     DISPOSABLE EMAIL DOMAIN BLOCKING
     ============================================================ */

  var disposableDomains = null;

  async function loadDisposableDomains() {
    if (disposableDomains) return;
    try {
      var el = document.querySelector('[data-disposable-domains]');
      if (el) {
        var resp = await fetch(el.getAttribute('data-disposable-domains'));
        if (resp.ok) {
          disposableDomains = await resp.json();
        } else {
          disposableDomains = [];
        }
      } else {
        disposableDomains = [];
      }
    } catch (e) {
      disposableDomains = [];
    }
  }

  function isDisposableEmail(email) {
    if (!disposableDomains || !disposableDomains.length) return false;
    var domain = email.split('@')[1];
    if (!domain) return false;
    return disposableDomains.indexOf(domain.toLowerCase()) !== -1;
  }


  /* ============================================================
     SUBMIT FORM VIA HIDDEN IFRAME
     
     Submits a real Shopify {% form %} element using a hidden 
     iframe as target. This allows Shopify's built-in CAPTCHA 
     system to work properly (it intercepts form submit events 
     and adds a reCAPTCHA token before the actual POST).
     
     formEl:    the <form> DOM element (from {% form 'customer' %})
     onSuccess: callback function on successful submission
     onError:   callback function(errorMsg) on failure
     ============================================================ */

  function submitForm(formEl, onSuccess, onError) {
    // Create hidden iframe
    var frameName = 'ls-submit-' + Date.now();
    var iframe = document.createElement('iframe');
    iframe.name = frameName;
    iframe.style.cssText = 'position:absolute;width:0;height:0;border:0;opacity:0;pointer-events:none;';
    document.body.appendChild(iframe);

    // Point form at iframe so page doesn't navigate
    formEl.target = frameName;

    // Timeout safety (15 seconds)
    var done = false;
    var timeout = setTimeout(function () {
      if (!done) {
        done = true;
        cleanup();
        onError('Request timed out. Please try again.');
      }
    }, 15000);

    function cleanup() {
      clearTimeout(timeout);
      formEl.removeAttribute('target');
      try { document.body.removeChild(iframe); } catch (e) {}
    }

    // Wait a tick for the iframe to be ready, then listen for load
    setTimeout(function () {
      iframe.addEventListener('load', function () {
        if (done) return;
        done = true;
        cleanup();
        onSuccess();
      });

      // Trigger native form submission
      // requestSubmit() fires submit event handlers (including Shopify's CAPTCHA)
      // form.submit() bypasses handlers — don't use it
      if (typeof formEl.requestSubmit === 'function') {
        formEl.requestSubmit();
      } else {
        // Fallback for older browsers: create and click a hidden submit button
        var tempBtn = document.createElement('button');
        tempBtn.type = 'submit';
        tempBtn.style.display = 'none';
        formEl.appendChild(tempBtn);
        tempBtn.click();
        formEl.removeChild(tempBtn);
      }
    }, 50);
  }


  /* ============================================================
     GA4 DATALAYER EVENTS
     ============================================================ */

  function fireEvent(eventName, params) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(Object.assign({ event: eventName }, params || {}));
  }


  /* ============================================================
     ENHANCED CONVERSIONS
     ============================================================ */

  function fireEnhancedConversion(email) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'enhanced_conversion_data',
      enhanced_conversions: { email: email.trim().toLowerCase() }
    });
  }


  /* ============================================================
     SCROLL LOCK / UNLOCK
     ============================================================ */

  var scrollPos = 0;

  function lockScroll() {
    scrollPos = window.pageYOffset || document.documentElement.scrollTop;
    document.body.classList.add('ls-popup-no-scroll');
    document.body.style.top = '-' + scrollPos + 'px';
  }

  function unlockScroll() {
    document.body.classList.remove('ls-popup-no-scroll');
    document.body.style.top = '';
    window.scrollTo(0, scrollPos);
  }


  /* ============================================================
     SUBMISSION STATE
     ============================================================ */

  function hasSubmitted() {
    return !!getLocal('liftsets_popup_submitted');
  }

  function markSubmitted() {
    setLocal('liftsets_popup_submitted', '1');
  }


  /* ============================================================
     VISIT COUNTER
     ============================================================ */

  function trackVisit() {
    if (!getSession('liftsets_visit_counted')) {
      var count = parseInt(getLocal('liftsets_visit_count') || '0', 10);
      setLocal('liftsets_visit_count', String(count + 1));
      setSession('liftsets_visit_counted', '1');
    }
  }

  function getVisitCount() {
    return parseInt(getLocal('liftsets_visit_count') || '0', 10);
  }


  /* ============================================================
     PAGE VIEW COUNTER (per session)
     ============================================================ */

  function trackPageView() {
    var views = parseInt(getSession('liftsets_page_views') || '0', 10);
    setSession('liftsets_page_views', String(views + 1));
  }

  function getPageViews() {
    return parseInt(getSession('liftsets_page_views') || '0', 10);
  }


  /* ============================================================
     PAGE EXCLUSION CHECK
     ============================================================ */

  function isExcludedPage() {
    var path = window.location.pathname.toLowerCase();
    return (
      path.indexOf('/cart') === 0 ||
      path.indexOf('/checkout') === 0 ||
      path.indexOf('/account') === 0 ||
      path.indexOf('/policies') === 0
    );
  }


  /* ============================================================
     LOGGED-IN CHECK
     ============================================================ */

  function isLoggedIn() {
    return !!window.__liftsets_logged_in;
  }


  /* ============================================================
     DISMISSAL COOLDOWN CHECK
     ============================================================ */

  function withinDismissalCooldown(storageKey, cooldownHours) {
    var dismissed = getLocal(storageKey);
    if (!dismissed) return false;
    var hoursSince = (Date.now() - parseInt(dismissed, 10)) / 3600000;
    return hoursSince < cooldownHours;
  }


  /* ============================================================
     UTM HELPERS
     ============================================================ */

  function getUTMParam(param) {
    try {
      var url = new URL(window.location.href);
      return url.searchParams.get(param) || '';
    } catch (e) {
      return '';
    }
  }

  function isBrandSearch() {
    var source = getUTMParam('utm_source').toLowerCase();
    var campaign = getUTMParam('utm_campaign').toLowerCase();
    return source === 'google' && campaign.indexOf('brand') !== -1;
  }


  /* ============================================================
     INITIALISE
     ============================================================ */

  trackVisit();
  trackPageView();


  /* ============================================================
     PUBLIC API
     ============================================================ */

  return {
    // Storage
    getLocal: getLocal,
    setLocal: setLocal,
    removeLocal: removeLocal,
    getSession: getSession,
    setSession: setSession,

    // Validation
    isValidEmail: isValidEmail,
    isDisposableEmail: isDisposableEmail,
    loadDisposableDomains: loadDisposableDomains,

    // Submission (iframe-based, works with Shopify CAPTCHA)
    submitForm: submitForm,

    // Events
    fireEvent: fireEvent,
    fireEnhancedConversion: fireEnhancedConversion,

    // Scroll management
    lockScroll: lockScroll,
    unlockScroll: unlockScroll,

    // State checks
    hasSubmitted: hasSubmitted,
    markSubmitted: markSubmitted,
    isExcludedPage: isExcludedPage,
    isLoggedIn: isLoggedIn,
    isBrandSearch: isBrandSearch,

    // Counters
    trackVisit: trackVisit,
    getVisitCount: getVisitCount,
    trackPageView: trackPageView,
    getPageViews: getPageViews,

    // Cooldown
    withinDismissalCooldown: withinDismissalCooldown,

    // UTM
    getUTMParam: getUTMParam
  };

})();